import { Injectable, isDevMode, NgZone } from "@angular/core";
import { Observable, BehaviorSubject, distinctUntilChanged, shareReplay, filter } from "rxjs";
import { AppStorageService, StorageLocation } from "@core/app-store/app-storage.service";
import { ThemeHelper, ThemeMode } from "@shared/helpers/theme/theme.helper";


/**
 * @description Service quản lý theme của ứng dụng (dành cho các UI framework: Taiga UI, ...)
 * @note
 * - Theo dõi thay đổi class trên <body> để tự động phát hiện khi theme (dark/light) đổi.
 * - Tách biệt xử lý trong NgZone.runOutsideAngular() để tránh render lại Angular không cần thiết.
 * @Upgrade 24/10/2025
 * @author Cường
 */
@Injectable({ providedIn: 'root' })
export class ThemeAppService {

  /**
   * Khởi tạo service lần đầu khi dùng ở APP_INITIALIZER
   * Chỉ áp dụng cho lần khởi tạo đầu tiên
   */
  init() {

    // Theo dõi class trên <body>
    const body = document.body as HTMLBodyElement;
    this.lastClassList = new Set(Array.from(body.classList));
    this.lastTheme = body.classList.contains('mod-skin-dark') ? 'dark' : body.classList.contains('mod-skin-light') ? 'light' : 'default';

    // Tạo MutationObserver
    /** Mục đích:
     * Chạy ngoài Angular zone để tránh re-render UI liên tục.
     * MutationObserver lắng nghe khi class trên <body> thay đổi.
     */
    this.ngZone.runOutsideAngular(() => {
      this.observer = new MutationObserver(() => {
        clearTimeout(this.debounceTimer);
        this.debounceTimer = setTimeout(() => this.handleClassChange(body), 100);
      });

      this.observer.observe(body, {
        attributes: true,
        attributeFilter: ['class'],
      });
    });
  };

  private observer?: MutationObserver;
  private lastClassList = new Set<string>();
  private lastTheme = ThemeMode.DEFAULT;
  private debounceTimer?: any;

  private modeTheme$$ = new BehaviorSubject<string>(ThemeHelper.getMode() ?? '');
  /**
   * ✅ Public Observable theo dõi thay đổi theme của ứng dụng
   */
  public themeMode$: Observable<string> = this.modeTheme$$.asObservable();

  constructor(
    private readonly storageService: AppStorageService,
    private ngZone: NgZone,
  ) {
  }

  ngOnDestroy(): void {
    this.observer?.disconnect();
    clearTimeout(this.debounceTimer);
  }

  private setThemeMode(mode: string) {
    this.modeTheme$$.next(mode);
  }

  private handleClassChange(body: HTMLBodyElement): void {
    const current = new Set(Array.from(body.classList));
    const added = [...current].filter(c => !this.lastClassList.has(c));
    const removed = [...this.lastClassList].filter(c => !current.has(c));

    if (added.length === 0 && removed.length === 0) return;

    // Cập nhật trạng thái
    this.lastClassList = current;

    // Publish sự kiện ra Observable
    for (const c of added) this.publishChange(c, true);
    for (const c of removed) this.publishChange(c, false);
  }

  private publishChange(className: string, added: boolean): void {

    // 🧠 Kiểm tra theme hiện tại
    const body = document.body;
    const hasDark = body.classList.contains('mod-skin-dark');
    const hasLight = body.classList.contains('mod-skin-light');

    let newTheme = ThemeMode.DEFAULT;
    if (hasDark) newTheme = ThemeMode.DARK;
    else if (hasLight) newTheme = ThemeMode.LIGHT;

    // ✅ Chỉ setThemeMode khi theme thực sự thay đổi
    if (this.lastTheme !== newTheme) {
      this.lastTheme = newTheme;
      isDevMode() && console.debug(`[BodyClass] Theme changed → ${newTheme}`);
      this.setThemeMode(newTheme);
    }
  }

}

export interface BodyClassState {
  className: string;
  added: boolean;
}
